import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { WeddingBooking, WeddingVendor } from '../../types';
import { format } from 'date-fns';
import { Calendar, MapPin, Phone, Mail, XCircle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface ExtendedWeddingBooking extends WeddingBooking {
    vendor: WeddingVendor;
}

export default function WeddingBookings() {
    const [bookings, setBookings] = useState<ExtendedWeddingBooking[]>([]);
    const [loading, setLoading] = useState(true);
    const [cancelingId, setCancelingId] = useState<string | null>(null);

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error } = await supabase
                .from('wedding_bookings')
                .select(`
                    *,
                    vendor:wedding_vendors(*)
                `)
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setBookings((data as any) || []);
        } catch (error) {
            console.error('Error fetching wedding bookings:', error);
            toast.error('Failed to load wedding bookings');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (bookingId: string) => {
        if (!confirm('Are you sure you want to cancel this booking request?')) return;

        setCancelingId(bookingId);
        try {
            const { error } = await supabase
                .from('wedding_bookings')
                .update({ status: 'cancelled' })
                .eq('id', bookingId);

            if (error) throw error;

            toast.success('Booking cancelled');
            setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: 'cancelled' } : b));
        } catch (error) {
            console.error('Error canceling booking:', error);
            toast.error('Failed to cancel booking');
        } finally {
            setCancelingId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-rose-600" />
            </div>
        );
    }

    if (bookings.length === 0) {
        return (
            <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No wedding bookings found</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-2">Start exploring vendors to plan your big day!</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {bookings.map((booking) => (
                <div
                    key={booking.id}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-all"
                >
                    <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-6">
                        {/* Image */}
                        <div className="w-full sm:w-32 h-32 flex-shrink-0 bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden">
                            {booking.vendor.images?.[0] ? (
                                <img
                                    src={booking.vendor.images[0]}
                                    alt={booking.vendor.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <MapPin size={24} />
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate pr-4">
                                        {booking.vendor.name}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-1">
                                        <MapPin size={14} />
                                        {booking.vendor.city || 'Location N/A'}
                                    </p>
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize
                                    ${booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                        booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                                'bg-gray-100 text-gray-700'
                                    }`}>
                                    {booking.status}
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm mt-4">
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                    <Calendar size={14} className="text-rose-500" />
                                    <span>{format(new Date(booking.booking_date), 'PPP')}</span>
                                    <span className="text-gray-400">({booking.booking_type.replace('_', ' ')})</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                    <span className="font-semibold">₹{booking.total_price.toLocaleString()}</span>
                                </div>
                                {booking.vendor.contact_phone && (
                                    <div className="flex items-center gap-2 text-gray-500">
                                        <Phone size={14} />
                                        {booking.vendor.contact_phone}
                                    </div>
                                )}
                                {booking.vendor.contact_email && (
                                    <div className="flex items-center gap-2 text-gray-500">
                                        <Mail size={14} />
                                        {booking.vendor.contact_email}
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            {booking.status === 'pending' && (
                                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                                    <button
                                        onClick={() => handleCancel(booking.id)}
                                        disabled={!!cancelingId}
                                        className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center gap-1 disabled:opacity-50"
                                    >
                                        {cancelingId === booking.id ? (
                                            <Loader2 size={14} className="animate-spin" />
                                        ) : (
                                            <XCircle size={14} />
                                        )}
                                        Cancel Request
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
